import { arrayMove } from '@dnd-kit/sortable';

import type {
  AnalysisCellResult,
  AnalysisResolvedSource,
  AnalysisCohortSource,
  AnalysisDefinition,
  AnalysisResultDataRow,
  AnalysisResultSectionHeader,
  AnalysisRow,
  AnalysisSectionHeader,
  AnalysisSource,
  TableGridColumn,
  TableGridRow,
  TableGridSource,
} from '../api/types';
import type { SelectedDatasetSource, StagedDefinitionEdit } from '../types';

const DEFAULT_COHORT_SOURCE_KEY = 'cohort';

const isCohortSource = (source: AnalysisSource): source is AnalysisCohortSource =>
  source.kind === 'cohort';

/**
 * Key of the first cohort entry in the definition's `sources` map — the
 * cohort the Source cohort and Study period sections show.
 */
export const getFirstCohortSourceKey = (definition: AnalysisDefinition | null): string | null => {
  if (!definition) return null;
  const entry = Object.entries(definition.sources).find(([, source]) => isCohortSource(source));
  return entry ? entry[0] : null;
};

export const getFirstCohortProfileId = (definition: AnalysisDefinition | null): string | null => {
  const key = getFirstCohortSourceKey(definition);
  if (!definition || !key) return null;
  const source = definition.sources[key];
  return isCohortSource(source) ? source.cohortProfileId : null;
};

/**
 * The analytical dataset profile behind the first cohort source (the
 * population) — the dataset whose per-patient results the "Analytical dataset"
 * table option shows. `null` when there is no cohort source or its dataset has
 * not been created yet.
 */
export const getFirstCohortAnalyticalDatasetProfileId = (
  definition: AnalysisDefinition | null
): string | null => {
  const key = getFirstCohortSourceKey(definition);
  if (!definition || !key) return null;
  const source = definition.sources[key];
  return isCohortSource(source) ? source.analyticalDatasetProfileId : null;
};

/** True when any table column is pinned to the cohort source named `sourceKey`. */
export const isCohortSourceInUse = (
  definition: AnalysisDefinition | null,
  sourceKey: string | null
): boolean => {
  if (!definition || !sourceKey) return false;
  return definition.tables.some((table) =>
    table.columns.some((column) => column.cohort === sourceKey)
  );
};

/** A key not already in `sources`, starting from `base` and suffixing
 * `_2`, `_3`, ... until free. */
export const nextFreeKey = (sources: AnalysisDefinition['sources'], base: string): string => {
  if (!(base in sources)) return base;
  let suffix = 2;
  while (`${base}_${suffix}` in sources) suffix += 1;
  return `${base}_${suffix}`;
};

/**
 * Returns a copy of `definition` whose first cohort source points at
 * `cohortProfileId`.
 *
 * - An existing cohort source keeps its key, so table columns pinned to it stay
 *   valid; its content hash is cleared because it described the previous cohort.
 * - With no cohort source, a new one is added under a free key.
 * - `null` removes the first cohort source. The server rejects a definition
 *   whose columns still reference a removed source, so callers gate removal on
 *   `isCohortSourceInUse`.
 */
export const applySourceCohort = (
  definition: AnalysisDefinition | null,
  cohortProfileId: string | null
): AnalysisDefinition => {
  const base: AnalysisDefinition = definition ?? { sources: {}, tables: [] };
  const key = getFirstCohortSourceKey(base);
  const sources = { ...base.sources };

  if (cohortProfileId === null) {
    if (key) delete sources[key];
    return { ...base, sources };
  }

  if (key) {
    const existing = sources[key] as AnalysisCohortSource;
    sources[key] = { ...existing, cohortProfileId, cohortContentHash: null };
  } else {
    sources[nextFreeKey(sources, DEFAULT_COHORT_SOURCE_KEY)] = {
      kind: 'cohort',
      cohortProfileId,
      analyticalDatasetProfileId: null,
      cohortContentHash: null,
    };
  }
  return { ...base, sources };
};

/** Whether `sources` already holds a variable source for the analytical variable. */
export const hasVariableSource = (sources: AnalysisDefinition['sources'], avId: string): boolean =>
  Object.values(sources).some((source) => source.kind === 'variable' && source.avId === avId);

/** `sources` with the analytical variable added under a free key; `sources` itself
 * when the variable is already there. */
export const addVariableSource = (
  sources: AnalysisDefinition['sources'],
  avId: string,
  description: string | null
): AnalysisDefinition['sources'] =>
  hasVariableSource(sources, avId)
    ? sources
    : {
        ...sources,
        [nextFreeKey(sources, avId)]: { kind: 'variable', avId, sqlHash: null, description },
      };

/** `sources` and `sourceOrder` without the source named `key`. */
export const removeSourceByKey = (
  sources: AnalysisDefinition['sources'],
  sourceOrder: string[],
  key: string
): { sources: AnalysisDefinition['sources']; sourceOrder: string[] } => {
  const remaining = { ...sources };
  delete remaining[key];
  return { sources: remaining, sourceOrder: sourceOrder.filter((name) => name !== key) };
};

/** The order with the source `activeKey` moved to the position of `overKey`;
 * `null` when either is not a source or they are the same one. */
export const moveSourceInOrder = (
  sources: AnalysisDefinition['sources'],
  sourceOrder: string[],
  activeKey: string,
  overKey: string
): string[] | null => {
  const order = getOrderedSourceNames({ sources, sourceOrder });
  const fromIndex = order.indexOf(activeKey);
  const toIndex = order.indexOf(overKey);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return null;
  return arrayMove(order, fromIndex, toIndex);
};

interface StagedEditState {
  sources: AnalysisDefinition['sources'];
  sourceOrder: string[];
  /** The cohort the staged definition attaches, or null for none. */
  cohortProfileId: string | null;
}

/** `state` with one `edit` applied; the input is not modified. */
export const applyStagedEdit = (
  state: StagedEditState,
  edit: StagedDefinitionEdit
): StagedEditState => {
  switch (edit.kind) {
    case 'setCohort':
      return { ...state, cohortProfileId: edit.cohortProfileId };
    case 'addVariable':
      return {
        ...state,
        sources: addVariableSource(state.sources, edit.avId, edit.description),
      };
  }
};

/** Number of data rows in `table` — section headers don't count as rows. */
export const countTableRows = (table: { rows: { kind: string }[] }): number =>
  table.rows.filter((row) => row.kind === 'row').length;

// Synthetic, UI-only entries every analytical dataset carries; they have no
// backing analytical variable, so they are not clickable or removable.
export interface DefaultDatasetVariable {
  key: string;
  typeLabel: string;
  nameMessageId: string;
  descriptionMessageId: string;
}

export const DEFAULT_DATASET_VARIABLES: ReadonlyArray<DefaultDatasetVariable> = [
  {
    key: '__default_patient_id__',
    typeLabel: 'STRING',
    nameMessageId: 'analysisDetail.tables.preview.defaultPatientId.name',
    descriptionMessageId: 'analysisDetail.tables.preview.defaultPatientId.description',
  },
  {
    key: '__default_index_date__',
    typeLabel: 'DATE',
    nameMessageId: 'analysisDetail.tables.preview.defaultIndexDate.name',
    descriptionMessageId: 'analysisDetail.tables.preview.defaultIndexDate.description',
  },
];

type SourceOrderInput = Pick<AnalysisDefinition, 'sources' | 'sourceOrder'>;

/**
 * Every name in `sources`, in the researcher's order. Names in `sourceOrder`
 * that are not sources and repeated names are skipped, and sources it leaves
 * out follow in `sources` order. Without a `sourceOrder` this is `sources`
 * order.
 */
export const getOrderedSourceNames = ({ sources, sourceOrder }: SourceOrderInput): string[] => {
  const names = Object.keys(sources);
  if (!sourceOrder || sourceOrder.length === 0) return names;
  const listed = [...new Set(sourceOrder)].filter((name) => name in sources);
  const listedNames = new Set(listed);
  return [...listed, ...names.filter((name) => !listedNames.has(name))];
};

/**
 * The `sourceOrder` to save: the complete order once one was set, and an
 * empty list while the analysis has none, so saving other edits does not
 * start an order the researcher never chose.
 */
export const getSavedSourceOrder = (definition: SourceOrderInput): string[] =>
  definition.sourceOrder && definition.sourceOrder.length > 0
    ? getOrderedSourceNames(definition)
    : [];

interface StagedDefinitionInput {
  /** The staged sources; the saved definition's sources when nothing is staged. */
  sources: AnalysisDefinition['sources'] | null;
  sourceOrder: string[];
  /** The saved definition the staged edits are applied onto. */
  savedDefinition: AnalysisDefinition | null;
  cohortProfileId: string | null;
}

/**
 * The definition to save for the staged edits. `tables` is not editable from the
 * definition screen, so it is carried over from the saved definition.
 */
export const buildStagedDefinition = ({
  sources,
  sourceOrder,
  savedDefinition,
  cohortProfileId,
}: StagedDefinitionInput): AnalysisDefinition => {
  const withCohort = applySourceCohort(
    {
      sources: sources ?? savedDefinition?.sources ?? {},
      tables: savedDefinition?.tables ?? [],
    },
    cohortProfileId
  );
  // Computed after the cohort edit so the order lists the final set of sources.
  return {
    ...withCohort,
    sourceOrder: getSavedSourceOrder({ sources: withCohort.sources, sourceOrder }),
  };
};

/**
 * The definition's variable and intent sources as listed in the "Analytical
 * dataset" preview, after the default variables, in the definition's source
 * order. Source names that point at the same analytical variable collapse into
 * one entry at the position of the first of those names, marked used when any
 * of them is used.
 */
export const getDatasetSources = (definition: AnalysisDefinition): SelectedDatasetSource[] => {
  const avIdToTableNumbers = getAvIdTableNumbers(definition);
  const seenAvIds = new Set<string>();
  return getOrderedSourceNames(definition).flatMap<SelectedDatasetSource>((key) => {
    const source = definition.sources[key];
    if (source.kind === 'intent') {
      return [
        {
          kind: 'intent',
          key,
          label: source.label,
          intent: source.intent,
          description: source.description ?? null,
          expectedOutputFormat: source.expectedOutputFormat ?? null,
          usedInTableNumbers: getNameTableNumbers(definition, key),
        },
      ];
    }
    if (source.kind !== 'variable' || seenAvIds.has(source.avId)) return [];
    seenAvIds.add(source.avId);
    return [
      {
        kind: 'variable',
        key,
        avId: source.avId,
        sqlHash: source.sqlHash,
        expectedOutputFormat: source.expectedOutputFormat ?? null,
        usedInTableNumbers: avIdToTableNumbers.get(source.avId) ?? [],
      },
    ];
  });
};

/**
 * Maps each variable source's `avId` to the 1-based positions of the tables
 * (in `definition.tables` order) that reference it -- through any source name
 * resolving to that `avId` -- via a column or row `uses` list. An `avId` used
 * by no table is left out of the map.
 */
const getAvIdTableNumbers = (definition: AnalysisDefinition): Map<string, number[]> => {
  const avIdByName = new Map(
    Object.entries(definition.sources).flatMap(([name, source]) =>
      source.kind === 'variable' ? [[name, source.avId] as const] : []
    )
  );
  const avIdToTableNumbers = new Map<string, number[]>();
  definition.tables.forEach((table, index) => {
    const tableNumber = index + 1;
    const avIdsInTable = new Set<string>();
    const collect = (names: string[]) =>
      names.forEach((name) => {
        const avId = avIdByName.get(name);
        if (avId) avIdsInTable.add(avId);
      });
    table.columns.forEach((column) => collect(column.uses));
    table.rows.forEach((row) => collect(row.uses ?? []));
    avIdsInTable.forEach((avId) => {
      const tableNumbers = avIdToTableNumbers.get(avId) ?? [];
      tableNumbers.push(tableNumber);
      avIdToTableNumbers.set(avId, tableNumbers);
    });
  });
  return avIdToTableNumbers;
};

/**
 * The 1-based positions of the tables (in `definition.tables` order) that
 * reference `name` directly in a row, section header or column's `uses` --
 * unlike `getAvIdTableNumbers`, no resolution through a variable's `avId`,
 * since an intent source is referenced by its own name until it becomes one.
 */
const getNameTableNumbers = (definition: AnalysisDefinition, name: string): number[] =>
  definition.tables.flatMap((table, index) => {
    const usesName = (names: string[]) => names.includes(name);
    const referenced =
      table.columns.some((column) => usesName(column.uses)) ||
      table.rows.some((row) => usesName(row.uses ?? []));
    return referenced ? [index + 1] : [];
  });

/**
 * The dataset sources a run's results cannot have a column for: intent
 * sources (not yet a variable) and variable sources whose `avId` the run did
 * not resolve -- i.e. added to the definition (saved or still staged) after
 * the run. With no run, every source qualifies.
 */
export const getSourcesMissingFromRun = (
  datasetSources: SelectedDatasetSource[],
  resolvedSources: Record<string, AnalysisResolvedSource> | undefined
): SelectedDatasetSource[] => {
  const runAvIds = new Set(
    Object.values(resolvedSources ?? {}).flatMap((source) =>
      source.kind === 'variable' ? [source.avId] : []
    )
  );
  return datasetSources.filter((source) => source.kind === 'intent' || !runAvIds.has(source.avId));
};

/** Number of cards in the "Analytical dataset" preview — the count shown for
 * that table option. */
export const countDatasetSources = (definition: AnalysisDefinition | null): number =>
  definition ? DEFAULT_DATASET_VARIABLES.length + getDatasetSources(definition).length : 0;

/** The label shown as a table column's header, or `null` when it is empty. */
export const getColumnGroupLabel = (column: TableGridColumn): string | null =>
  column.columnLabel || null;

/**
 * The analytical-variable id a `uses` list links to: the first entry that
 * names a `variable` source, or `null` when it has none. Shared by section
 * headers and data rows -- both only ever reference a variable source via
 * `uses` (see `SectionHeader`/`AnalysisRow` in analysis_schemas.py).
 */
export const getUsesAvId = (
  uses: string[],
  sources: AnalysisDefinition['sources']
): string | null => {
  for (const name of uses) {
    const source = sources[name];
    if (source?.kind === 'variable') return source.avId;
  }
  return null;
};

interface UsesRow {
  uses: string[];
  parentRowId: string | null;
}

const hasUses = (
  row: TableGridRow
): row is
  | AnalysisRow
  | AnalysisSectionHeader
  | AnalysisResultDataRow
  | AnalysisResultSectionHeader => 'uses' in row;

/** Every row/section header in `rows` keyed by its own `rowId` -- rows without
 * one (a definition persisted before `rowId`/`parentRowId` existed) are left
 * out, since nothing can name them as a parent. */
export const buildRowsById = (rows: TableGridRow[]): Map<string, UsesRow> => {
  const byId = new Map<string, UsesRow>();
  for (const row of rows) {
    if (hasUses(row) && (row.kind === 'row' || row.rowId)) byId.set(row.rowId as string, row);
  }
  return byId;
};

/**
 * The one source name a row/header genuinely owns -- present in its own
 * `uses` but not inherited from its parent section header's `uses`. A
 * numeric/string section's statistic or level rows (e.g. "...Mean (SD)"
 * under "Age at index date") share the header's variable rather than owning
 * one, so this returns `null` for them; the header itself, and a row with no
 * section-level sharing (e.g. "Hallucinations"), each own their single entry.
 *
 * `null` for zero or more than one such entry too, so a row is never treated
 * as ambiguously linking to more than one variable.
 */
export const ownedSourceName = (
  row: UsesRow,
  rowsById: ReadonlyMap<string, UsesRow>
): string | null => {
  const parent = row.parentRowId ? rowsById.get(row.parentRowId) : undefined;
  const inherited = new Set(parent?.uses ?? []);
  const own = row.uses.filter((name) => !inherited.has(name));
  return own.length === 1 ? own[0] : null;
};

/**
 * The analytical variable a table column header links to: the first `uses`
 * entry that names a `variable` source -- the filter variable that defines a
 * subgroup column -- or `null`. A full-cohort column carries no such variable,
 * so it has no link.
 */
export const getColumnLinkAvId = (
  column: TableGridColumn,
  sources: Record<string, TableGridSource>
): string | null => {
  for (const name of column.uses ?? []) {
    const source = sources[name];
    if (source?.kind === 'variable') return source.avId;
  }
  return null;
};

/**
 * The populated cell of `row` under `columnId`, or `null` for a section
 * header, a definition row (which has no cells) or a column not populated yet.
 */
export const getRowCell = (row: TableGridRow, columnId: string): AnalysisCellResult | null =>
  'cells' in row ? row.cells[columnId] ?? null : null;
